import React, { useState, useEffect } from 'react';
import { List, Avatar, Badge, Button, Popover, Typography, Empty, Drawer } from 'antd';
import { BellOutlined, CheckCircleOutlined, InfoCircleOutlined, WarningOutlined, CloseCircleOutlined, ClearOutlined } from '@ant-design/icons';
import { useInAppNotifications } from '../context/InAppNotificationContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const NotificationPane = ({ compact = false }) => {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useInAppNotifications();
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleVisibleChange = (newVisible) => {
        setVisible(newVisible);
    };

    const handleViewAll = () => {
        setVisible(false);
        navigate('/notifications');
    };

    const handleItemClick = (item) => {
        if (!item.is_read) markAsRead(item.id);
        if (item.link) {
            setVisible(false);
            navigate(item.link);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'warning': return <WarningOutlined style={{ color: '#faad14' }} />;
            case 'error': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            default: return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
        }
    };

    const notificationContent = (
        <div style={{ width: isMobile ? '100%' : 350 }}>
            {!isMobile && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={5} style={{ margin: 0 }}>Notifications</Title>
                    {unreadCount > 0 && (
                        <Button type="link" size="small" onClick={markAllAsRead} icon={<ClearOutlined />}>
                            Mark all as read
                        </Button>
                    )}
                </div>
            )}
            <div style={{ maxHeight: isMobile ? 'calc(100vh - 150px)' : 400, overflowY: 'auto' }}>
                {notifications.length > 0 ? (
                    <List
                        itemLayout="horizontal"
                        dataSource={notifications}
                        renderItem={item => (
                            <List.Item
                                onClick={() => handleItemClick(item)}
                                style={{ 
                                    padding: '12px 16px', 
                                    cursor: 'pointer', 
                                    backgroundColor: item.is_read ? 'transparent' : '#e6f7ff',
                                    transition: 'background-color 0.3s'
                                }}
                            >
                                <List.Item.Meta
                                    avatar={<Avatar icon={getIcon(item.type)} style={{ backgroundColor: 'transparent' }} />}
                                    title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text strong={!item.is_read}>{item.title}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {dayjs(item.created_at).fromNow()}
                                            </Text>
                                        </div>
                                    }
                                    description={
                                        <Text type={item.is_read ? "secondary" : "default"} style={{ fontSize: '13px' }}>
                                            {item.message}
                                        </Text>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <div style={{ padding: '40px 0' }}>
                        <Empty description="No notifications" />
                    </div>
                )}
            </div>
            <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                <Button type="link" size="small" onClick={handleViewAll}>View All Notifications</Button>
            </div>
        </div>
    );

    const trigger = (
        <Badge count={unreadCount} overflowCount={99} size="small" offset={compact ? [5, 5] : [-2, 10]}>
            <Button 
                type="text" 
                icon={<BellOutlined style={{ fontSize: compact ? '22px' : '20px' }} />} 
                style={{ 
                    height: compact ? 'auto' : '64px', 
                    width: compact ? 'auto' : '64px',
                    padding: compact ? 0 : '4px 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={() => isMobile && setVisible(true)}
            />
        </Badge>
    );

    if (isMobile) {
        return (
            <>
                {trigger}
                <Drawer
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Notifications</span>
                            {unreadCount > 0 && (
                                <Button type="link" size="small" onClick={markAllAsRead} icon={<ClearOutlined />}>
                                    Mark all
                                </Button>
                            )}
                        </div>
                    }
                    placement="bottom"
                    onClose={() => setVisible(false)}
                    open={visible}
                    height="80vh"
                    styles={{ body: { padding: 0 } }}
                >
                    {notificationContent}
                </Drawer>
            </>
        );
    }

    return (
        <Popover
            content={notificationContent}
            trigger="click"
            placement="bottomRight"
            overlayStyle={{ padding: 0 }}
            open={visible}
            onOpenChange={handleVisibleChange}
        >
            {trigger}
        </Popover>
    );
};

export default NotificationPane;
